import Button, {ButtonProps} from 'components/Button';
import {FC, ReactNode, useEffect, useState} from 'react';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';

import {useCachedPDFContext} from '../context';

const PreviewButton: FC<{isActive?: boolean} & ButtonProps> = ({
    className,
    disabled,
    isActive,
    ...props
}) => (
    <Button
        {...props}
        className={twMerge(
            className,
            'border-none hover:bg-grey-200',
            isActive && 'bg-grey-300 hover:bg-grey-200',
            disabled && 'cursor-not-allowed text-grey-500',
        )}
        kind="custom"
        size="sm"
    />
);

const PageNavigation = () => {
    const context = useCachedPDFContext();

    const hasNextPage = context.currentPage < context.maxPages;
    const hasPreviousPage = context.currentPage > 1;

    return (
        <>
            <PreviewButton
                aria-label={t`Previous page`}
                disabled={!hasPreviousPage}
                icon="arrow_up"
                onClick={() => {
                    context.gotoPage(context.currentPage - 1);
                }}
            />
            <PreviewButton
                aria-label={t`Next page`}
                disabled={!hasNextPage}
                icon="arrow_down"
                onClick={() => {
                    context.gotoPage(context.currentPage + 1);
                }}
            />
        </>
    );
};
const EditableInput = () => {
    const context = useCachedPDFContext();
    const [focused, setFocused] = useState(false);
    const [controlledValue, setControlledValue] = useState<string>(
        context.currentPage.toString(),
    );

    useEffect(() => {
        if (focused) return;
        setControlledValue(context.currentPage.toString());
    }, [context.currentPage]);

    return (
        <>
            <input
                className="my-0 h-8 max-w-14 py-0 text-sm"
                maxLength={100}
                onBlur={() => setFocused(false)}
                onChange={(event) => {
                    const value = Number(event.target.value);
                    if (focused) {
                        if (isNaN(value)) return;
                        setControlledValue(event.target.value);
                    }
                    context.gotoPage(value);
                }}
                onFocus={() => setFocused(true)}
                type="text"
                value={controlledValue}
            />
            &nbsp; / {context.maxPages}
        </>
    );
};

const PagePreviewControls = () => {
    const context = useCachedPDFContext();

    return (
        <PreviewButton
            aria-label={t`Toggle page previews`}
            icon="blocks"
            isActive={context.showPagePreviews}
            onClick={() =>
                context._setShowPagePreviews(!context.showPagePreviews)
            }
        />
    );
};

const ScaleControls = () => {
    const context = useCachedPDFContext();

    return (
        <>
            <PreviewButton
                aria-label={t`Zoom out`}
                icon="minus"
                onClick={context._scaleDown}
            />
            <PreviewButton
                aria-label={t`Zoom in`}
                icon="plus"
                onClick={context._scaleUp}
            />
        </>
    );
};
const Section: FC<{children: ReactNode}> = ({children}) => (
    <div className="border-r border-r-grey-300 px-4 last:border-r-0">
        {children}
    </div>
);

type ControlOption = 'page-previews' | 'page' | 'scale';
const CachedPDFControls: FC<{
    className?: string;
    include?: ControlOption[];
}> = ({className, include = ['page', 'page-previews']}) => {
    return (
        <div
            className={twMerge('flex bg-grey-100 p-1', className)}
            id="cached-pdf-controls"
        >
            {include.includes('page-previews') && (
                <Section>
                    <PagePreviewControls />
                </Section>
            )}
            {include.includes('page') && (
                <>
                    <Section>
                        <PageNavigation />
                    </Section>
                    <Section>
                        <EditableInput />
                    </Section>
                </>
            )}
            <div className="flex-1" />
            {include.includes('scale') && (
                <Section>
                    <ScaleControls />
                </Section>
            )}
        </div>
    );
};
export default CachedPDFControls;
